import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { toast } from 'react-toastify';
import ToggleSwitch from '../components/ToggleSwitch';

const EMPTY_FORM = { label: '', path: '', openInNewTab: false, requiresAuth: false, order: 0 };

export default function ManageMenu() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchItems = () => {
    setLoading(true);
    API.get('/menu-items/admin').then(r => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchItems(); }, []);

  const resetForm = () => { setForm(EMPTY_FORM); setEditingId(null); };
  const openAddForm = () => { resetForm(); setShowForm(true); };
  const openEditForm = (item) => {
    setEditingId(item._id);
    setForm({
      label: item.label, path: item.path, openInNewTab: !!item.openInNewTab,
      requiresAuth: !!item.requiresAuth, order: item.order || 0,
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); resetForm(); };

  const handleSave = async () => {
    if (!form.label.trim()) return toast.error('Label is required');
    if (!form.path.trim()) return toast.error('Link (path or URL) is required');
    setSaving(true);
    try {
      if (editingId) {
        await API.put(`/menu-items/${editingId}`, form);
        toast.success('Menu item updated');
      } else {
        await API.post('/menu-items', form);
        toast.success('Menu item added');
      }
      closeForm();
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving');
    }
    setSaving(false);
  };

  const handleTogglePublish = async (item) => {
    try {
      await API.put(`/menu-items/${item._id}`, { isActive: !item.isActive });
      toast.success(item.isActive ? 'Hidden from menu' : 'Shown in menu');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error updating status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this menu item? This cannot be undone.')) return;
    try {
      await API.delete(`/menu-items/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 className="admin-page-title" style={{ margin: 0 }}>🧭 Manage Menu</h2>
        <button className="btn btn-primary" onClick={() => (showForm ? closeForm() : openAddForm())}>
          {showForm ? 'Cancel' : '+ Add Menu Item'}
        </button>
      </div>
      <p style={{ color: '#888', fontSize: '0.85rem', marginBottom: 24 }}>
        Controls the links shown in the header navigation (and its mobile menu). Link to any existing page (e.g. <code>/gallery</code>) or an external URL (e.g. <code>https://...</code>).
      </p>

      {showForm && (
        <div className="admin-form-card" style={{ marginBottom: 24 }}>
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>{editingId ? 'Edit Menu Item' : 'Add Menu Item'}</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Label *</label>
              <input className="form-input" value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="Gallery" />
            </div>
            <div className="form-group">
              <label className="form-label">Link (path or URL) *</label>
              <input className="form-input" value={form.path} onChange={e => setForm(p => ({ ...p, path: e.target.value }))} placeholder="/gallery or https://..." />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <ToggleSwitch checked={form.openInNewTab} onChange={v => setForm(p => ({ ...p, openInNewTab: v }))} onLabel="Opens in new tab" offLabel="Opens in same tab" />
            </div>
            <div className="form-group">
              <ToggleSwitch checked={form.requiresAuth} onChange={v => setForm(p => ({ ...p, requiresAuth: v }))} onLabel="Logged-in users only" offLabel="Visible to everyone" />
            </div>
          </div>
          <div className="form-group" style={{ maxWidth: 160 }}>
            <label className="form-label">Display Order</label>
            <input className="form-input" type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update' : 'Save'}
            </button>
            <button className="btn btn-outline" onClick={closeForm}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="loader"><div className="spinner" /></div> : (
        <div className="admin-table">
          <table>
            <thead>
              <tr>
                <th>Order</th><th>Label</th><th>Link</th><th>Login Only</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item._id} style={{ opacity: item.isActive ? 1 : 0.6 }}>
                  <td>{item.order}</td>
                  <td style={{ fontWeight: 600 }}>{item.label}</td>
                  <td style={{ fontSize: '0.85rem', color: '#888' }}>
                    {item.path}{item.openInNewTab && ' ↗'}
                  </td>
                  <td>{item.requiresAuth ? '✅' : '—'}</td>
                  <td>
                    <ToggleSwitch checked={item.isActive} onChange={() => handleTogglePublish(item)} onLabel="Shown" offLabel="Hidden" />
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button title="Edit" onClick={() => openEditForm(item)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>✏️</button>
                      <button title="Delete" onClick={() => handleDelete(item._id)} style={{ background: 'none', border: '1px solid #f5c6c6', color: '#c62828', borderRadius: 6, width: 30, height: 30, cursor: 'pointer' }}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: '#888' }}>No menu items yet</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
